import { cn } from "@/shared/lib/utils";
import { SeeMoreSection } from "../SeeMoreSection";
import { useStoreNavigation } from "../storeNavigation";
import type { BarListItem } from "../types";

interface SectionBarListProps {
  title: string;
  items: BarListItem[];
  /** Opt-in see-more: constant visible head + count line + one-tap reveal-all. */
  head?: number;
  /** Count-line noun for the see-more count line. Default "rows". */
  noun?: string;
  /** Rendered instead of the list when items is empty. */
  emptyState?: string;
}

/**
 * Ranked horizontal bar list — Tufte-style sparkbar with the value inline.
 * Use when row identity matters more than a grouped chart.
 */
export function SectionBarList({ items, head, noun, emptyState }: SectionBarListProps) {
  const nav = useStoreNavigation();

  if (items.length === 0 && emptyState) {
    return <p className="px-5 py-4 text-sm text-muted-foreground">{emptyState}</p>;
  }

  // Bar widths normalize against the max of the FULL set, never just the
  // visible see-more slice.
  const universalMax = Math.max(
    1,
    ...items.map((i) => i.max ?? i.value),
  );

  const renderItem = (it: BarListItem, i: number) => {
    const max = it.max ?? universalMax;
    const widthPct = `${Math.min(100, (it.value / max) * 100)}%`;
    const storeNumber = it.store_number;
    const openStore = nav && storeNumber ? () => nav.openStore(storeNumber, it.label) : null;
    const rowBody = (
      <>
        <span
          className={cn(
            "w-32 shrink-0 truncate text-foreground/90",
            openStore && "text-left",
          )}
        >
          {it.label}
          {it.sublabel ? (
            <span className="text-muted-foreground"> {it.sublabel}</span>
          ) : null}
        </span>
        <span className="relative flex-1 h-2 rounded-sm bg-muted/40 overflow-hidden">
          <span
            className="absolute inset-y-0 left-0 rounded-sm bg-primary/80"
            style={{ width: widthPct }}
          />
        </span>
        <span className="w-16 text-right tabular-nums text-muted-foreground">
          {it.value.toLocaleString()}
        </span>
      </>
    );
    if (openStore) {
      return (
        <li key={i}>
          <button
            type="button"
            onClick={openStore}
            className={cn(
              "flex w-full items-center gap-3 rounded-sm text-sm",
              "cursor-pointer hover:bg-accent/40 focus-visible:outline-none focus-visible:bg-accent/40",
            )}
          >
            {rowBody}
          </button>
        </li>
      );
    }
    return (
      <li key={i} className="flex items-center gap-3 text-sm">
        {rowBody}
      </li>
    );
  };

  if (head != null) {
    return (
      <SeeMoreSection head={head} total={items.length} noun={noun ?? "rows"}>
        {({ start, end }) => (
          <ul className={cn("px-5 space-y-2", start === 0 ? "pt-4 pb-1" : "pt-1 pb-1")}>
            {items.slice(start, end).map((it, i) => renderItem(it, start + i))}
          </ul>
        )}
      </SeeMoreSection>
    );
  }

  return <ul className="px-5 py-4 space-y-2">{items.map(renderItem)}</ul>;
}
